import { useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { useIntl } from "react-intl";
import { Navigate } from "react-router";
import { BoxHeader } from "@/components/design/BoxHeader";
import { Button } from "@/components/design/Button";
import { AdminMasterDetail } from "@/components/admin/AdminMasterDetail";
import { MenuItem } from "@/components/design/MenuItem";
import { KpiBadge } from "@/components/design/KpiBadge";
import { Disclosure } from "@/components/design/Disclosure";
import { InfoTooltip } from "@/components/design/InfoTooltip";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { UnsavedChangesDialog } from "@/components/common/UnsavedChangesDialog";
import { usePendingScope } from "@/lib/pending-edits";
import { useUnsavedEditsGate } from "@/lib/unsaved-edits-gate";
import { LocalizedError } from "@/lib/localized-error";
import { TextInput } from "@/components/design/TextField";
import { AccessRoleCatalogDocument, CreateAccessRoleDocument, UpdateAccessRoleDocument, DeleteAccessRoleDocument,
  type AccessRoleDetailsFragment } from "@/graphql/generated/operations";
import { errorClassification, userErrorMessage } from "@/lib/user-error";
import { AccessRoleAssignmentMembers } from "./AccessRoleAssignmentMembers";

export function AccessRolesPanel() {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, string | number>) => intl.formatMessage({ id }, values);
  const { data, loading, error } = useQuery(AccessRoleCatalogDocument, { fetchPolicy: "network-only" });
  const [createRole, { loading: createPending }] = useMutation(CreateAccessRoleDocument);
  const [updateRole, { loading: updatePending }] = useMutation(UpdateAccessRoleDocument);
  const [deleteRole, { loading: deletePending }] = useMutation(DeleteAccessRoleDocument);
  const saving = createPending || updatePending || deletePending;
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AccessRoleDetailsFragment | null>(null);
  const [deleting, setDeleting] = useState<AccessRoleDetailsFragment | null>(null);
  const formOpen = creating || !!editing;
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [activeRoleId, setActiveRoleId] = useState<string | null>(null);
  const submitInFlight = useRef(false);
  const savePromise = useRef<Promise<void> | null>(null);
  const leaveGate = useUnsavedEditsGate();
  const createButton = useRef<HTMLButtonElement>(null);
  const permissions = data?.accessPermissions.permissions ?? [];
  const roles = [...(data?.accessRoles.roles ?? [])].sort((a, b) =>
    Number(a.isSystem) - Number(b.isSystem)
    || a.name.localeCompare(b.name, intl.locale, { sensitivity: "base", numeric: true })
    || a.id.localeCompare(b.id));
  const activeRole = roles.find((role) => role.id === activeRoleId) ?? roles[0];
  const hasDraft = !!name || !!description || selected.length > 0;
  const dirty = editing
    ? name !== editing.name || description !== (editing.description ?? "")
      || selected.length !== editing.permissions.length
      || editing.permissions.some((permission) => !selected.includes(permission.id))
    : hasDraft;
  const query = search.trim().toLocaleLowerCase(intl.locale);
  const visiblePermissions = (formOpen ? permissions : activeRole?.permissions ?? [])
    .filter((permission) => [permission.code, permission.description]
      .some((value) => value.toLocaleLowerCase(intl.locale).includes(query)))
    .sort((a, b) => a.code.localeCompare(b.code, intl.locale));
  usePendingScope("access-role-editor", { dirty, save: submit, discard: closeForm });

  function closeForm() {
    setCreating(false);
    setName("");
    setDescription("");
    setSelected([]);
    setEditing(null);
    setSearch("");
    setSaveError(null);
    window.requestAnimationFrame(() => createButton.current?.focus());
  }

  function beginEdit(role: AccessRoleDetailsFragment) {
    if (role.isSystem || role.isInUse || saving) return;
    setActiveRoleId(role.id);
    setCreating(false);
    setEditing(role);
    setName(role.name);
    setDescription(role.description ?? "");
    setSelected(role.permissions.map((permission) => permission.id));
    setSearch("");
    setSaveError(null);
    setCreatedId(null);
  }

  function submit(): Promise<void> {
    // SaveBar and a leave dialog can ask for the same save while a mutation is pending.
    // Both must await its outcome, never treat an in-flight write as a successful no-op.
    if (savePromise.current) return savePromise.current;
    const promise = persistDraft().finally(() => { savePromise.current = null; });
    savePromise.current = promise;
    return promise;
  }

  async function persistDraft() {
    if (!dirty) return;
    if (!name.trim() || name.trim().length > 120 || description.trim().length > 2000) {
      throw new LocalizedError(t("admin.roles.invalidFields"));
    }
    if (!selected.length || !selected.every((id) => permissions.some((permission) => permission.id === id))) {
      throw new LocalizedError(t("admin.roles.permissionsRequired"));
    }
    if (loading || error || submitInFlight.current) throw new LocalizedError(t("common.saveFailed"));
    submitInFlight.current = true;
    setSaveError(null);
    try {
      if (editing) {
        const result = await updateRole({
          variables: { input: { id: editing.id, name: name.trim(),
            description: description.trim() || null, permissionIds: selected } },
          update(cache, { data: response }) {
            if (!response?.updateAccessRole) return;
            cache.updateQuery({ query: AccessRoleCatalogDocument }, (previous) => previous ? {
              ...previous, accessRoles: { ...previous.accessRoles,
                roles: previous.accessRoles.roles.map((role) => role.id === response.updateAccessRole.id ? response.updateAccessRole : role) },
            } : previous);
          },
        });
        if (!result.data?.updateAccessRole) throw new LocalizedError(t("common.saveFailed"));
        closeForm();
        return;
      }
      const result = await createRole({
        variables: { input: { name: name.trim(), description: description.trim() || null, permissionIds: selected } },
        update(cache, { data: response }) {
          if (!response?.createAccessRole) return;
          cache.updateQuery({ query: AccessRoleCatalogDocument }, (previous) => previous ? {
            ...previous,
            accessRoles: { ...previous.accessRoles, roles: [response.createAccessRole,
              ...previous.accessRoles.roles.filter((role) => role.id !== response.createAccessRole.id)] },
          } : previous);
        },
      });
      if (result.data?.createAccessRole) {
        setCreatedId(result.data.createAccessRole.id);
        setActiveRoleId(result.data.createAccessRole.id);
        closeForm();
      } else throw new LocalizedError(t("common.saveFailed"));
    } catch (cause) {
      const message = cause instanceof LocalizedError ? cause.message : userErrorMessage(intl, cause);
      setSaveError(message);
      throw new LocalizedError(message, { cause });
    } finally {
      submitInFlight.current = false;
    }
  }

  async function confirmDelete() {
    if (!deleting || deleting.isSystem || deleting.isInUse || submitInFlight.current) return;
    submitInFlight.current = true;
    setSaveError(null);
    try {
      const target = deleting;
      const result = await deleteRole({ variables: { input: { id: target.id } },
        update(cache, { data: response }) {
          if (!response?.deleteAccessRole) return;
          cache.updateQuery({ query: AccessRoleCatalogDocument }, (previous) => previous ? {
            ...previous, accessRoles: { ...previous.accessRoles, roles: previous.accessRoles.roles.filter((role) => role.id !== target.id) },
          } : previous);
        },
      });
      if (result.data?.deleteAccessRole) { setDeleting(null); setActiveRoleId(null); setCreatedId(null); }
    } catch (cause) {
      setSaveError(userErrorMessage(intl, cause));
    } finally { submitInFlight.current = false; }
  }

  const quickFilter = <TextInput type="search" aria-label={t("admin.roles.search")} placeholder={t("admin.roles.search")}
    value={search} onInput={(event) => setSearch(event.currentTarget.value)}
    onChange={(event) => setSearch(event.target.value)}
    onKeyDown={(event) => { if (event.key === "Enter") event.preventDefault(); }} />;

  if (error && errorClassification(error) === "FORBIDDEN") return <Navigate to="/" replace />;

  return (
    <AdminMasterDetail title={t("admin.roles.listTitle")} count={roles.length} list={<>
      <Button ref={createButton} className="mb-4 w-full" aria-label={t(hasDraft && !formOpen ? "admin.roles.resumeDraft" : "admin.roles.new")}
        disabled={loading || !!error || formOpen || saving || permissions.length === 0}
        onClick={() => { setCreating(true); setCreatedId(null); }}>
        {hasDraft && !formOpen ? t("admin.roles.resumeDraft") : t("admin.roles.addRole")}
      </Button>
      {loading && <p role="status" className="text-sm text-ink/55">{t("admin.roles.loading")}</p>}
      {error && <p role="alert" className="text-sm text-ink/70">{userErrorMessage(intl, error)}</p>}
      {!loading && !error && roles.length === 0 && <p className="text-sm text-ink/70">{t("admin.roles.empty")}</p>}
      {!error && <nav aria-label={t("admin.roles.existing")} className="space-y-1">
        {[false, true].map((system) => {
          const group = roles.filter((role) => role.isSystem === system);
          return group.length > 0 && <div key={String(system)} className="mb-4">
            <h3 className="px-2 py-2 text-xs font-medium text-ink/55">{t(system ? "admin.roles.systemRoles" : "admin.roles.customRoles")}</h3>
            {group.map((role) => <MenuItem key={role.id} aria-label={role.name} title={role.name}
              aria-description={t("admin.roles.peopleCount", { count: role.assignedPersonCount })}
              active={!creating && role.id === activeRole?.id} disabled={saving || !!editing}
              trailing={<span className="text-xs tabular-nums text-ink/55">{t("admin.roles.peopleCount", { count: role.assignedPersonCount })}</span>}
              onClick={() => { setActiveRoleId(role.id); setCreating(false); setCreatedId(null); setSaveError(null); }}>
              <span className="block truncate">{role.name}</span>
            </MenuItem>)}
          </div>;
        })}
      </nav>}
    </>}>

      {formOpen && (
        <section className="box min-w-0 bg-surface p-6" aria-label={t(editing ? "admin.roles.edit" : "admin.roles.new")}>
          <BoxHeader title={t(editing ? "admin.roles.edit" : "admin.roles.new")} />
          <form onSubmit={(event) => event.preventDefault()}>
            <fieldset disabled={saving} className="space-y-6">
              <div className="grid grid-cols-1 gap-4">
              <label className="block min-w-0">
                <span className="field-label">{t("admin.roles.name")}</span>
                <TextInput autoFocus required maxLength={120} value={name} className="mt-1"
                  placeholder={t("admin.roles.namePlaceholder")} onChange={(event) => setName(event.target.value)} />
              </label>
              <label className="block min-w-0">
                <span className="field-label">{t("admin.roles.description")}</span>
                <TextInput maxLength={2000} value={description} className="mt-1"
                  placeholder={t("admin.roles.descriptionPlaceholder")}
                  onChange={(event) => setDescription(event.target.value)} />
              </label>
              </div>
              <fieldset className="space-y-4 border-t border-ink/10 pt-6">
                <legend className="float-left mb-4 w-full text-sm font-medium">{t("admin.roles.assignPermissions")}</legend>
                <div className="clear-both">{quickFilter}</div>
                <div>
                  {visiblePermissions.map((permission) => {
                    const checked = selected.includes(permission.id);
                    return <label key={permission.id}
                      className={`flex cursor-pointer items-start gap-4 border-b border-ink/10 px-2 py-2 transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ink/30 ${checked ? "bg-ink/10" : "hover:bg-ink/10"}`}>
                      <input type="checkbox" aria-label={permission.code}
                        className="mt-1 size-4 shrink-0 accent-ink focus-visible:outline-none" checked={checked}
                        onChange={() => setSelected((previous) => previous.includes(permission.id)
                          ? previous.filter((id) => id !== permission.id) : [...previous, permission.id])} />
                      <span className="min-w-0">
                        <span className="block break-all text-sm font-medium text-ink">{permission.code}</span>
                        <span className="mt-1 block break-words text-xs leading-relaxed text-ink/70">{permission.description}</span>
                      </span>
                    </label>;
                  })}
                </div>
                {visiblePermissions.length === 0 && <div className="py-8 text-sm text-ink/70">
                  <p>{t("admin.roles.noResults")}</p>
                  <Button type="button" variant="ghost" className="mt-2" onClick={() => setSearch("")}>{t("admin.roles.clearSearch")}</Button>
                </div>}
              </fieldset>
              <div className="space-y-2 border-t border-ink/10 pt-4">
                <p role="status" className="text-sm font-medium">{t("admin.roles.selected", { count: selected.length })}</p>
                <p className="max-w-[42rem] text-xs leading-relaxed text-ink/70">{t("admin.roles.unassigned")}</p>
              </div>
              {saveError && <p role="alert" className="text-sm text-ink/70">{saveError}</p>}
              <div className="flex flex-wrap justify-end gap-4">
                <Button type="button" variant="secondary" onClick={() => leaveGate.request(closeForm)}>{t("common.cancel")}</Button>
              </div>
            </fieldset>
          </form>
        </section>
      )}

      {data && !error && !formOpen && (
        <section className="box min-w-0 bg-surface p-6" aria-label={t("admin.roles.details")}>
          <BoxHeader title={<span className="break-all">{activeRole?.name ?? t("admin.roles.title")}</span>}
            className="flex-wrap"
            right={activeRole && (activeRole.isSystem || activeRole.isInUse) ? <KpiBadge tone="muted"
              info={{ title: t("admin.roles.readOnly"), ariaLabel: t("admin.roles.readOnly"), content: t(activeRole.isSystem ? "admin.roles.systemProtected" : "admin.roles.inUseProtected") }}>
              {t("admin.roles.readOnly")}
            </KpiBadge> : undefined} />
          {activeRole ? <>
              {!activeRole.isSystem && !activeRole.isInUse && <div className="mb-4 flex flex-wrap gap-2">
                <Button variant="secondary" size="compact" disabled={saving} onClick={() => leaveGate.request(() => beginEdit(activeRole))}>{t("admin.roles.edit")}</Button>
                <Button variant="ghost" size="compact" disabled={saving} onClick={() => { setDeleting(activeRole); setSaveError(null); }}>{t("admin.roles.delete")}</Button>
              </div>}
            {createdId === activeRole.id && <p role="status" className="mb-4 text-sm text-ink/70">{t("admin.roles.created")}</p>}
            {activeRole.description && <p className="mb-6 text-sm text-ink/70">{activeRole.description}</p>}
            <h3 className="mb-4 text-sm font-medium">{t("admin.roles.permissions")} <span className="text-ink/55">· {activeRole.permissions.length}</span></h3>
            {quickFilter}
            {query && <p role="status" className="mt-2 text-xs text-ink/55">{t("admin.roles.shown", { shown: visiblePermissions.length, total: activeRole.permissions.length })}</p>}
            <ul className="mt-4 divide-y divide-ink/10">
              {visiblePermissions.map((permission) => <li key={permission.id} className="py-2">
                <p className="break-all text-sm font-medium">{permission.code}</p>
                <p className="mt-1 break-words text-xs leading-relaxed text-ink/70">{permission.description}</p>
              </li>)}
            </ul>
            {visiblePermissions.length === 0 && <div className="space-y-2 py-4">
              <p className="text-sm text-ink/70">{t("admin.roles.noRoleResults")}</p>
              {query && <Button variant="ghost" onClick={() => setSearch("")}>{t("admin.roles.clearSearch")}</Button>}
            </div>}
            <section aria-label={t("admin.roles.assignments")} className="mt-6">
              <Disclosure key={activeRole.id} title={t("admin.roles.assignments")}
                count={t("admin.roles.peopleCount", { count: activeRole.assignedPersonCount })}
                headerAction={<InfoTooltip ariaLabel={t("admin.roles.assignments")}>{t("admin.roles.assignmentsHint")}</InfoTooltip>}>
              {activeRole.assignments.length === 0 ? <p className="mt-2 text-xs text-ink/70">{t("admin.roles.noAssignments")}</p>
                : <ul className="mt-4 space-y-2">{activeRole.assignments.map((assignment) =>
                  <li key={`${activeRole.id}-${assignment.grantId}-${assignment.segmentId ?? "none"}`}>
                    {assignment.segmentId ? <AccessRoleAssignmentMembers roleId={activeRole.id}
                      segmentId={assignment.segmentId} segmentName={assignment.segmentName ?? t("admin.roles.noSegment")}
                      audienceName={assignment.audienceName} personCount={assignment.personCount} />
                      : <div className="flex flex-wrap items-center justify-between gap-2 py-3">
                    <div className="min-w-0">
                      <p className="break-words text-sm">{assignment.segmentName ?? t("admin.roles.noSegment")}</p>
                      <p className="mt-1 text-xs text-ink/55">{t("admin.roles.scope", { name: assignment.audienceName })}</p>
                    </div>
                    <span className="text-xs text-ink/70">{assignment.segmentId
                      ? t("admin.roles.peopleCount", { count: assignment.personCount }) : t("admin.roles.grantOnly")}</span>
                      </div>}
                  </li>)}</ul>}
              </Disclosure>
            </section>
          </> : <p className="text-sm text-ink/70">{t("admin.roles.intro")}</p>}
        </section>
      )}
      <UnsavedChangesDialog open={leaveGate.blocked} saving={leaveGate.saving} error={leaveGate.error}
        onStay={leaveGate.stay} onDiscard={leaveGate.discardAndProceed} onSave={leaveGate.saveAndProceed} />
      <ConfirmDialog open={!!deleting} title={t("admin.roles.delete")}
        message={<><p>{t("admin.roles.deleteConfirm", { name: deleting?.name ?? "" })}</p>
          {saveError && <p role="alert" className="mt-4">{saveError}</p>}</>}
        confirmLabel={t(deletePending ? "admin.roles.deleting" : "admin.roles.delete")}
        onCancel={() => { if (!submitInFlight.current) { setDeleting(null); setSaveError(null); } }}
        onConfirm={confirmDelete} />
    </AdminMasterDetail>
  );
}
